import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosResponse } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { OnResultFunction, QrReader } from 'react-qr-reader';
import { ViewFinder } from '../ViewFinder';
import { API } from '../api';
import { RootState } from '../store';
import { addCode, registerCompany } from '../store/qrSlice';
import { ApiVoucher, TypeCompanyInfo, VoucherParams } from '../types';
import { getCompanyType } from '../companyTypes';
import { PageWrapper } from '../PageWrapper';
import { useInvoice } from '../context/InvoiceContext';

const SCAN_WARMUP_MS = 1000;

function getVoucherParams(value: string) {
    const splittedUrl = value.split('/');
    const idWithToken = splittedUrl.pop()?.split('?token=');

    return { id: idWithToken?.[0], token: idWithToken?.[1] };
}

function isVoucherPayload(value: string) {
    const { id } = getVoucherParams(value);
    return Boolean(id && /^\d+$/.test(id));
}

function isPartnerPayload(value: string) {
    try {
        const linkData = JSON.parse(value);
        return Boolean(linkData?.type && linkData?.key);
    } catch {
        return false;
    }
}

function serverMessage(data: unknown): string | undefined {
    if (!data || typeof data !== 'object') return undefined;

    const body = data as { message?: unknown; error?: unknown };
    if (typeof body.message === 'string' && body.message) return body.message;
    if (typeof body.error === 'string' && body.error) return body.error;
    return undefined;
}

function linkFailureState(error: unknown): {
    status?: number;
    message: string;
} {
    if (axios.isAxiosError(error)) {
        return {
            status: error.response?.status,
            message:
                serverMessage(error.response?.data) ||
                error.message ||
                'Link request failed',
        };
    }

    if (error instanceof Error && error.message) {
        return { message: error.message };
    }

    return { message: 'Link request failed' };
}

export const QRScan = () => {
    const [finderColor, setFinderColor] =
        useState<string>('rgba(0, 0, 0, 0.5)');
    const dispatch = useDispatch();
    const companyInfo = useSelector((state: RootState) => state.qr.company);
    const scannedVouchers = useSelector((state: RootState) => state.qr.codes);
    const navigate = useNavigate();
    const { setVoucher, setAmountRequired, setReceiptRequired, setMaxAmount } =
        useInvoice();
    const isRequesting = useRef<boolean>(false);
    const cooldownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastScanText = useRef<string | null>(null);
    const focusApplied = useRef(false);
    const cameraReadyAt = useRef<number | null>(null);

    const applyContinuousFocus = () => {
        if (focusApplied.current) return;

        const video = document.getElementById(
            'qrScan'
        ) as HTMLVideoElement | null;
        const track = (
            video?.srcObject as MediaStream | null
        )?.getVideoTracks?.()[0];
        if (!track?.getCapabilities) return;

        focusApplied.current = true;
        const { focusMode } =
            track.getCapabilities() as MediaTrackCapabilities & {
                focusMode?: string[];
            };
        if (!focusMode?.includes('continuous')) return;

        track
            .applyConstraints({
                advanced: [
                    { focusMode: 'continuous' } as MediaTrackConstraintSet,
                ],
            })
            .catch(() => undefined);
    };

    const noteCameraStarted = () => {
        if (cameraReadyAt.current !== null) return;

        const video = document.getElementById(
            'qrScan'
        ) as HTMLVideoElement | null;
        if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
            return;
        }

        cameraReadyAt.current = Date.now();
    };

    const isCameraWarmingUp = () =>
        cameraReadyAt.current === null ||
        Date.now() - cameraReadyAt.current < SCAN_WARMUP_MS;

    const onCompanyLinkScan = (data: string) => {
        let linkData = null;
        try {
            linkData = JSON.parse(data);
        } catch {
            setFinderColor('rgba(255, 0, 0, 0.5)');
            return false;
        }

        axios
            .post(`${API}users/${linkData.type}/link`, {
                key: linkData.key,
            })
            .then((response) => {
                const body =
                    response.data && typeof response.data === 'object'
                        ? (response.data as {
                              id?: string | number;
                              name?: unknown;
                              companyType?: unknown;
                          })
                        : {};
                const name = body.name;
                const companyType = body.companyType;
                const token = response.headers?.authorization;
                const missing: string[] = [];

                if (!body.id) missing.push('id');
                if (typeof name !== 'string' || !name) missing.push('name');
                if (typeof companyType !== 'string' || !companyType) {
                    missing.push('companyType');
                }
                if (typeof token !== 'string' || !token) {
                    missing.push('Authorization');
                }

                if (
                    missing.length > 0 ||
                    typeof name !== 'string' ||
                    typeof companyType !== 'string' ||
                    typeof token !== 'string'
                ) {
                    setFinderColor('rgba(255, 0, 0, 0.5)');
                    navigate('/error', {
                        state: {
                            message: `Link response missing ${missing.join(', ')}`,
                        },
                    });
                    return;
                }

                const scannedData: TypeCompanyInfo = {
                    id: String(body.id),
                    name,
                    type: companyType as TypeCompanyInfo['type'],
                    token,
                };

                dispatch(registerCompany(scannedData));
                navigate('/linked');
            })
            .catch((error: unknown) => {
                setFinderColor('rgba(255, 0, 0, 0.5)');
                navigate('/error', { state: linkFailureState(error) });
            })
            .finally(() => {
                isRequesting.current = false;
                addCooldown();
            });

        return true;
    };

    const processVoucher = (
        response: AxiosResponse,
        voucherParams: VoucherParams,
        companyToken: string,
        url: string
    ) => {
        const resData = response.data;
        const voucher: ApiVoucher = resData.voucher;
        const isAmountRequired = !!resData.amountRequired;
        const isReceiptRequired = !!resData.receiptRequired;

        if (voucher) {
            if (isAmountRequired || isReceiptRequired) {
                setMaxAmount(voucher.value || null);
                setAmountRequired(isAmountRequired);
                setReceiptRequired(isReceiptRequired);
                setVoucher(voucherParams);
                navigate('/invoice-details');
            } else {
                axios
                    .patch(
                        url,
                        {
                            securityToken: voucherParams.token,
                            usedAmount: voucher.value,
                        },
                        {
                            headers: {
                                Authorization: companyToken,
                            },
                        }
                    )
                    .then(() => {
                        dispatch(addCode(voucher.id));
                        navigate('/success');
                    })
                    .catch(() => {
                        setFinderColor('rgba(255, 0, 0, 0.5)');
                        navigate('/error');
                    })
                    .finally(() => {
                        isRequesting.current = false;
                        addCooldown();
                    });
            }
        } else if (resData.status === 'Already activated') {
            setFinderColor('rgba(255, 0, 0, 0.5)');
            navigate('/fail');
        } else if (resData.status === 'Invalid voucher') {
            setFinderColor('rgba(255, 0, 0, 0.5)');
            navigate('/error');
        }
    };

    const onVoucherScan = (value: string) => {
        const voucherParams = getVoucherParams(value);
        const companyToken = companyInfo?.token;
        const companyType = getCompanyType(companyInfo?.type);
        const { id, token } = voucherParams;
        const regex = /^\d+$/;

        if (!id || !regex.test(id)) {
            setFinderColor('rgba(255, 0, 0, 0.5)');
            isRequesting.current = false;
            return;
        }

        if (scannedVouchers.indexOf(Number(id)) === -1) {
            const url = `${API}${companyType}-vouchers/${id}`;
            axios
                .post(
                    url,
                    {
                        securityToken: token,
                    },
                    {
                        headers: {
                            Authorization: companyToken,
                        },
                    }
                )
                .then((response) => {
                    processVoucher(
                        response,
                        voucherParams,
                        companyToken || '',
                        url
                    );
                })
                .catch(() => {
                    setFinderColor('rgba(255, 0, 0, 0.5)');
                    navigate('/error');
                });
        } else {
            navigate('/fail');
        }
    };

    const addCooldown = () => {
        cooldownTimeoutRef.current = setTimeout(() => {
            cooldownTimeoutRef.current = null;
        }, 1000);
    };

    const handleResult: OnResultFunction = (result) => {
        applyContinuousFocus();
        noteCameraStarted();

        if (isCameraWarmingUp() || cooldownTimeoutRef.current) return;

        if (!result) {
            lastScanText.current = null;
            setFinderColor('rgba(0, 0, 0, 0.5)');
            return;
        }

        if (isRequesting.current) return;

        const text = result.getText();

        if (lastScanText.current !== text) {
            lastScanText.current = text;
            return;
        }

        lastScanText.current = null;

        if (companyInfo?.name) {
            if (!isVoucherPayload(text)) {
                setFinderColor('rgba(255, 0, 0, 0.5)');
                return;
            }

            isRequesting.current = true;
            onVoucherScan(text);
            setFinderColor('rgba(0, 128, 0, 0.5)');
            return;
        }

        if (!isPartnerPayload(text)) {
            setFinderColor('rgba(255, 0, 0, 0.5)');
            return;
        }

        isRequesting.current = true;
        if (!onCompanyLinkScan(text)) {
            isRequesting.current = false;
            return;
        }

        setFinderColor('rgba(0, 128, 0, 0.5)');
    };

    return (
        <PageWrapper bgColor="#000000">
            <QrReader
                ViewFinder={() => {
                    return <ViewFinder color={finderColor} />;
                }}
                scanDelay={500}
                onResult={handleResult}
                constraints={{
                    facingMode: 'environment',
                }}
                containerStyle={{ height: '100vh', width: '100%' }}
                videoContainerStyle={{
                    position: 'relative',
                    height: '100vh',
                    width: '100%',
                    padding: 0,
                }}
                videoStyle={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100vh',
                    width: '100%',
                    objectFit: 'cover',
                }}
                videoId="qrScan"
            />
            <Box
                sx={{
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'start',
                    justifyItems: 'center',
                    width: '100%',
                    height: '110px',
                    bottom: 0,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}
            >
                {companyInfo?.name ? (
                    <>
                        <Typography
                            variant="body2"
                            sx={{ fontSize: '.8em', alignSelf: 'center' }}
                        >
                            Linked to
                        </Typography>
                        <StoreIcon
                            sx={{ fontSize: '1.5em', alignSelf: 'center' }}
                        />
                        <Typography
                            variant="body2"
                            sx={{ fontSize: '.8em', alignSelf: 'center' }}
                        >
                            {companyInfo.name}
                        </Typography>
                    </>
                ) : (
                    <>
                        <QrCodeScannerIcon
                            sx={{ fontSize: '1.5em', alignSelf: 'center' }}
                        />
                        <Typography
                            variant="body2"
                            sx={{ fontSize: '.8em', alignSelf: 'center' }}
                        >
                            Please scan your <b>Partner Identifier QR-Code</b>
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ fontSize: '.8em', alignSelf: 'center' }}
                        >
                            to link this device to your account
                        </Typography>
                    </>
                )}
            </Box>
        </PageWrapper>
    );
};
